import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { serviceDesksApi, type ServiceDeskCampaignOption } from '../api/serviceDesksApi';

export interface CampaignOption {
  value: string;
  label: string;
}

export const serviceDeskCampaignsQueryKey = (id: number | null) => ['service-desks', id, 'campaigns'];

/**
 * Fetches enabled campaign types for a service desk and returns them as ready-to-use
 * {value, label} options. No fetch happens until a service desk id is provided.
 */
export const useServiceDeskCampaigns = (serviceDeskId: number | null) => {
  const enabled = serviceDeskId != null;

  const { data, isLoading, error } = useQuery<Record<string, ServiceDeskCampaignOption>>({
    queryKey: serviceDeskCampaignsQueryKey(serviceDeskId),
    queryFn: () => serviceDesksApi.campaigns(serviceDeskId as number),
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  const options = useMemo<CampaignOption[]>(
    () =>
      Object.entries(data ?? {}).map(([value, c]) => ({
        value,
        label: c.label ?? value,
      })),
    [data]
  );

  return { options, isLoading, error: error as Error | null };
};
